import { f7 } from 'framework7-react';
import { FC, useEffect, useState } from 'react';
import {
  getSubscriptionInheritanceStatusApi,
  getSubscriptionPlansApi,
  getSubscriptionStatusApi,
} from '@/api/subscription';
import fvHeroImage from '@/assets/images/subscription/fv-hero.png';
import fvOfferBanner from '@/assets/images/subscription/fv-offer-banner.png';
import fvStatIconLeft from '@/assets/images/subscription/fv-stat-icon-1.png';
import fvStatIconRight from '@/assets/images/subscription/fv-stat-icon-2.png';
import fvSubImage from '@/assets/images/subscription/fv-sub.png';
import pointsBadge from '@/assets/images/subscription/points-badge.png';
import supportCarlifeAssist from '@/assets/images/subscription/support-carlife-assist.png';
import supportLightRepair from '@/assets/images/subscription/support-light-repair.png';
import supportRoadService from '@/assets/images/subscription/support-road-service.png';
import { PageTemplate } from '@/components/uiParts/Template/PageTemplate/PageTemplate';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { SubscriptionPlan } from '@/types/api/subscription';

// --- LP訴求データ（ハードコード） ---

const painPoints = [
  {
    title: '車の修理代、突然10万円超えることも',
    description: 'エンジン修理、部品代だけで数万円…',
  },
  {
    title: '小傷・へこみ、放置すると査定にひびく',
    description: '小さな傷やへこみ、放置するのも気になる',
  },
  {
    title: '車検・点検のたびに予想外の追加費用がかかる',
    description: '車検・点検のたびに追加費用が発生する',
  },
];

const serviceContents = [
  {
    title: '修理優待',
    image: supportCarlifeAssist,
    description: '提携工場での対象整備・対象修理に会員価格を適用',
    included: [
      'エンジン・駆動系・電装系・ブレーキ系・サスペンション等',
      '事前見積承認制（工賃優待中心）',
      '部品: 中古→リビルト→社外新品→純正の優先順',
    ],
    excluded: [
      '消耗品（タイヤ・バッテリー・ブレーキパッド等）',
      '事故の鈑金全面修理・社外品・競技用部品',
    ],
  },
  {
    title: '軽補修優待',
    image: supportLightRepair,
    description: '提携工場での対象軽補修メニューに会員価格を適用',
    included: [
      '小傷・ガラスリペア・軽微なへこみ・ホイール補修',
      '事前見積承認制',
    ],
    excluded: ['板金全面・大事故修理・部品交換を伴う大規模修理'],
  },
  {
    title: 'ロードサービス',
    image: supportRoadService,
    description: '路上トラブルにも頼れる安心を',
    included: [
      'レッカー50kmまでの無料搬送',
      '鍵開け・バッテリー上がり・ガス欠・パンク時の応急対応',
      '24時間365日対応',
    ],
    excluded: ['事故処理・レッカー50km超過分の搬送費用'],
  },
];

const targetCategories = [
  'エンジン系',
  '駆動系',
  '電装系',
  'ブレーキ系',
  'ガラスリペア',
  '軽微なへこみ',
];

const repairExamples = [
  {
    title: 'スライドドアが動かなくなった',
    car: 'HONDA ステップワゴン（平成28年式）',
    mileage: '走行 72,000km',
    detail: '修理内容: スライドドアモーター交換',
    originalPrice: 140000,
  },
  {
    title: 'エアコンが効かなくなった',
    car: 'TOYOTA ヴォクシー（平成30年式）',
    mileage: '走行 65,000km',
    detail: '修理内容: エアコンコンプレッサー交換',
    originalPrice: 140000,
  },
  {
    title: 'パワーウィンドウが動かない',
    car: 'NISSAN セレナ（平成29年式）',
    mileage: '走行 80,000km',
    detail: '修理内容: パワーウィンドウモーター交換',
    originalPrice: 140000,
  },
];

const customerVoices = [
  {
    title: '12年落ちの車でも会員になれた',
    body: '12年落ちの軽。エンジン警告灯が点いてディーラーへ。修理見積は約¥8.8万。他社の保証サービスは年式で断られたがmaikuruは加入でき、会員価格で対応してもらえた。',
    profile: '40代・男性・軽自動車 12年目',
  },
  {
    title: '進捗がアプリで全部わかった',
    body: 'ATトランスミッション交換。通常¥15万超の修理が、会員価格で対応。アプリで見積確認→承認まで完結。工場への電話は一切不要だった。',
    profile: '30代・女性・コンパクトカー 8年目',
  },
];

const enrollmentSteps = [
  { step: '01', title: 'アプリをダウンロード' },
  { step: '02', title: '会員登録・車検証登録' },
  { step: '03', title: 'メンバーシップに加入' },
];

const faqItems = [
  {
    question: 'どの車でも加入できますか？',
    answer:
      '国産車も輸入車も、年式・走行距離を問わずご加入いただけます。保証適用には条件がございますので、詳しくは利用規約をご確認ください。',
  },
  {
    question: '加入したその日から使えますか？',
    answer: 'はい、ご加入いただいた当日からサービスをご利用いただけます。',
  },
  {
    question: '費用はどのくらいかかりますか？',
    answer: null, // 金額はplanから動的に生成
  },
  {
    question: '加入できない車や対象外の修理はありますか？',
    answer:
      '消耗品（タイヤ・バッテリー・ブレーキパッド等）や事故の鈑金全面修理・社外品・競技用部品は対象外です。詳しくは利用規約をご確認ください。',
  },
  {
    question: '解約の条件はありますか？',
    answer: 'いつでも解約が可能です。解約後は翌月からサービスが停止されます。',
  },
  {
    question: '加入後に返金はできますか？',
    answer:
      '月額料金の日割り返金は行っておりません。解約月の末日までサービスをご利用いただけます。',
  },
  {
    question: '支払いが失敗した場合はどうなりますか？',
    answer:
      '月内に3〜4回、引き落としを再試行します。それでも失敗した場合、2ヶ月後にサービスが停止されます。お申し込み時にお支払い方法をご確認ください。',
  },
];

// --- セクションヘッダー（洗車ページパターン流用） ---

const SectionHead: FC<{
  enTitle: string;
  jaTitle: string;
  accentColor?: string;
}> = ({ enTitle, jaTitle, accentColor = '#1E55E6' }) => (
  <div
    style={{
      textAlign: 'center',
      paddingBottom: '14px',
      marginBottom: '24px',
      position: 'relative',
    }}
  >
    <div
      style={{
        fontSize: '28px',
        fontWeight: 700,
        color: accentColor,
        lineHeight: 1.5,
      }}
    >
      {enTitle}
    </div>
    <div style={{ fontWeight: 700, lineHeight: 1.5, color: '#222' }}>
      {jaTitle}
    </div>
    <div
      style={{
        position: 'absolute',
        width: '26px',
        height: '2px',
        backgroundColor: accentColor,
        bottom: 0,
        left: 'calc(50% - 13px)',
      }}
    />
  </div>
);

// --- コンポーネント ---

export const SubscriptionDetailPage: FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    let unmounted = false;
    let redirecting = false;

    // F7ルーター遷移アニメーション中は reloadCurrent が無視されるため
    // アニメーション完了を待ってからナビゲーションする
    const navigateWhenReady = (path: string, opts: Record<string, unknown>) => {
      redirecting = true;
      const doNav = () => {
        if (unmounted) return;
        if ((f7.view.main.router as any).animating) {
          requestAnimationFrame(doNav);
          return;
        }
        f7.view.main.router.navigate(path, opts);
      };
      doNav();
    };

    const fetchPlans = async () => {
      try {
        let res = await getSubscriptionPlansApi();
        if (!res?.data?.success) {
          console.warn('プラン取得: 1回目失敗（success=false）、リトライ');
          res = await getSubscriptionPlansApi();
        }
        if (res?.data?.success && res?.data?.data) {
          if (!unmounted) setPlans(res.data.data.plans);
        } else {
          console.error('プラン取得失敗', res?.data);
        }
      } catch (e) {
        console.warn('プラン取得: 1回目例外、リトライ', e);
        try {
          const retry = await getSubscriptionPlansApi();
          if (retry?.data?.success && retry?.data?.data) {
            if (!unmounted) setPlans(retry.data.data.plans);
          } else {
            console.error('プラン取得失敗（リトライ後）', retry?.data);
          }
        } catch (e2) {
          console.error('プラン取得失敗（リトライ後例外）', e2);
        }
      }
    };

    const init = async () => {
      try {
        // 契約済みの場合はマイページにリダイレクト
        try {
          const authInfo = store.state.authInfo as any;
          const selectedCar = store.state.carItemInfo as any;
          if (authInfo?.m_customer_id) {
            // 選択中の車両で契約状態を取得（失敗時1回リトライ）
            const fetchStatus = async () => {
              try {
                const res = await getSubscriptionStatusApi(
                  authInfo.m_customer_id,
                  selectedCar?.t_stock_car_id,
                );
                if (res?.data?.success) return res;
              } catch {
                // 1回目失敗 → リトライ
              }
              return getSubscriptionStatusApi(
                authInfo.m_customer_id,
                selectedCar?.t_stock_car_id,
              );
            };
            const statusRes = await fetchStatus();
            let apiSuccess = !!statusRes?.data?.success;
            let currentSubscription =
              statusRes?.data?.data?.subscription ?? null;
            let pendingSubscription =
              statusRes?.data?.data?.pending_subscription || null;

            // 車両IDで見つからない場合、車両ID なしで未決済のサブスクを検索
            // （申込後に車検証未登録で t_stock_car_id が NULL のケース）
            let fromFallback = false;
            if (!currentSubscription && !pendingSubscription) {
              const globalRes = await getSubscriptionStatusApi(
                authInfo.m_customer_id,
              );
              if (globalRes?.data?.success) {
                apiSuccess = true;
                currentSubscription =
                  globalRes?.data?.data?.subscription ?? null;
                pendingSubscription =
                  globalRes?.data?.data?.pending_subscription || null;
                fromFallback = true;
              }
            }

            const noticeSubscription =
              pendingSubscription || currentSubscription;

            if (apiSuccess && noticeSubscription) {
              if (noticeSubscription.apply_status === 2) {
                navigateWhenReady(paths.subscriptionPayment, {
                  reloadCurrent: true,
                  props: {
                    subscriptionId: noticeSubscription.t_subscription_id,
                  },
                });
                return;
              }

              if (
                noticeSubscription.apply_status === 1 ||
                (noticeSubscription.apply_status === 0 &&
                  noticeSubscription.status === 2)
              ) {
                navigateWhenReady(paths.subscriptionComplete, {
                  reloadCurrent: true,
                  props: {
                    subscriptionId: noticeSubscription.t_subscription_id,
                  },
                });
                return;
              }

              // フォールバック由来：既知の条件にマッチしなくても
              // サブスクが存在する場合は complete に遷移（二重申込防止）
              if (fromFallback) {
                navigateWhenReady(paths.subscriptionComplete, {
                  reloadCurrent: true,
                  props: {
                    subscriptionId: noticeSubscription.t_subscription_id,
                  },
                });
                return;
              }
            }

            // 車両IDで直接見つかったアクティブなサブスクのみ mypage へ
            if (apiSuccess && currentSubscription && !fromFallback) {
              navigateWhenReady(paths.subscriptionMyPage, {
                reloadCurrent: true,
              });
              return;
            }

            if (selectedCar?.t_stock_car_id) {
              const inheritanceRes = await getSubscriptionInheritanceStatusApi(
                authInfo.m_customer_id,
                selectedCar.t_stock_car_id,
              );
              const inheritanceStatus = inheritanceRes?.data?.data;
              if (
                inheritanceRes?.data?.success &&
                inheritanceStatus?.eligible
              ) {
                navigateWhenReady(paths.subscriptionInheritance, {
                  reloadCurrent: true,
                  props: { inheritanceStatus },
                });
                return;
              }
            }
          }
        } catch (e) {
          console.error('サブスク状態チェックエラー', e);
        }

        await fetchPlans();
      } finally {
        if (redirecting) {
          // リダイレクト成功ならページ遷移で unmounted=true になる
          // 失敗した場合はプランを取得して表示にフォールバック
          setTimeout(async () => {
            if (unmounted) return;
            console.warn('リダイレクト未完了、プラン取得にフォールバック');
            await fetchPlans();
            if (!unmounted) setIsLoading(false);
          }, 2000);
        } else {
          setIsLoading(false);
        }
      }
    };
    init();

    return () => {
      unmounted = true;
    };
  }, []);

  const handleClickStart = () => {
    f7.view.main.router.navigate(paths.subscriptionConfirm);
  };

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  if (isLoading) {
    return (
      <PageTemplate
        pageName="subscription-detail"
        title="マイくるプラス"
        showNavBack
      >
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          読み込み中...
        </div>
      </PageTemplate>
    );
  }

  const plan = plans[0];

  if (!plan) {
    return (
      <PageTemplate
        pageName="subscription-detail"
        title="マイくるプラス"
        showNavBack
      >
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          現在利用可能なプランはありません
        </div>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate
      pageName="subscription-detail"
      title="マイくるプラス"
      showNavBack
    >
      <style>{`
        [name="subscription-detail"] .page-content > div {
          --background-color: #F5F5F7 !important;
          background-color: #F5F5F7 !important;
        }
      `}</style>
      <div style={{ margin: '0 -16px', padding: '0 16px' }}>
        {/* ===== S1: ファーストビュー ===== */}
        <div style={{ padding: '32px 0 24px', textAlign: 'center' }}>
          <p
            style={{
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#1E55E6',
              marginBottom: '12px',
            }}
          >
            月 1,980円 税込 で 最大20万円 のオトク
          </p>
          <h1
            style={{
              fontSize: '24px',
              fontWeight: 'bold',
              lineHeight: 1.4,
              marginBottom: '20px',
            }}
          >
            クルマをもっと
            <br />
            手軽に楽しもう
          </h1>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '20px',
            }}
          >
            <img
              src={fvHeroImage}
              alt="マイくるプラス アプリ画面（トラブル）"
              style={{
                width: '45%',
                maxWidth: '180px',
                height: 'auto',
                display: 'block',
              }}
            />
            <img
              src={fvSubImage}
              alt="マイくるプラス アプリ画面（車両情報）"
              style={{
                width: '45%',
                maxWidth: '180px',
                height: 'auto',
                display: 'block',
              }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px',
            }}
          >
            <img
              src={fvStatIconLeft}
              alt=""
              style={{ width: '18px', height: 'auto' }}
            />
            <div style={{ display: 'flex', gap: '24px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#888' }}>
                  顧客満足度
                </div>
                <div style={{ fontSize: '20px', fontWeight: 'bold' }}>92%</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#888' }}>
                  修理対応実績
                </div>
                <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                  30万件超
                </div>
              </div>
            </div>
            <img
              src={fvStatIconRight}
              alt=""
              style={{
                width: '18px',
                height: 'auto',
                transform: 'scaleX(-1)',
              }}
            />
          </div>
          <p
            style={{
              fontSize: '14px',
              color: '#555',
              lineHeight: 1.7,
              marginBottom: '20px',
            }}
          >
            REVISE提携工場での修理が優待価格に。
            <br />
            全車種OK・加入当日から・いつでも解約。
          </p>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '24px',
            }}
          >
            {['全車種OK', '加入当日から', 'アプリ完結'].map((tag) => (
              <span
                key={tag}
                style={{
                  display: 'inline-block',
                  padding: '4px 14px',
                  fontSize: '12px',
                  color: '#333',
                  backgroundColor: '#fff',
                  borderRadius: '20px',
                  border: '1px solid #ddd',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
          <img
            src={fvOfferBanner}
            alt="マイくるプラスのサービス"
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
              marginBottom: '20px',
            }}
          />
          <button
            onClick={handleClickStart}
            style={{
              width: '100%',
              padding: '16px',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#fff',
              backgroundColor: '#1a1a1a',
              border: 'none',
              borderRadius: '28px',
              cursor: 'pointer',
            }}
          >
            アプリから今すぐ加入！
          </button>
        </div>

        {/* ===== S2: 課題提起 ===== */}
        <div style={{ paddingBottom: '24px' }}>
          <SectionHead enTitle="PROBLEM" jaTitle="こんな経験、ありませんか？" />
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            {painPoints.map((pain, i) => (
              <div
                key={i}
                style={{
                  padding: '20px',
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                  }}
                >
                  <div
                    style={{
                      width: '3px',
                      minHeight: '40px',
                      backgroundColor: '#1a1a1a',
                      borderRadius: '2px',
                      flexShrink: 0,
                    }}
                  />
                  <div>
                    <div
                      style={{
                        fontSize: '15px',
                        fontWeight: 'bold',
                        marginBottom: '6px',
                      }}
                    >
                      {pain.title}
                    </div>
                    <div style={{ fontSize: '13px', color: '#888' }}>
                      {pain.description}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ===== S3: サービス内容 ===== */}
        <div style={{ paddingBottom: '24px' }}>
          <SectionHead enTitle="SERVICE" jaTitle="サービス内容" />
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            {serviceContents.map((service, i) => (
              <div
                key={i}
                style={{
                  padding: '20px',
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                }}
              >
                <img
                  src={service.image}
                  alt={service.title}
                  style={{
                    width: '100%',
                    height: '160px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    display: 'block',
                  }}
                />
                <div
                  style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                  }}
                >
                  {service.title}
                </div>
                <div
                  style={{
                    fontSize: '13px',
                    color: '#666',
                    marginBottom: '16px',
                  }}
                >
                  {service.description}
                </div>
                {service.included.map((item, j) => (
                  <div
                    key={`inc-${j}`}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                      fontSize: '13px',
                      marginBottom: '6px',
                    }}
                  >
                    <span style={{ color: '#1a1a1a', flexShrink: 0 }}>●</span>
                    <span>{item}</span>
                  </div>
                ))}
                {service.excluded.map((item, j) => (
                  <div
                    key={`exc-${j}`}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                      fontSize: '13px',
                      marginBottom: '6px',
                      color: '#C62828',
                    }}
                  >
                    <span style={{ flexShrink: 0 }}>×</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* 価格 */}
          <div
            style={{
              marginTop: '16px',
              padding: '24px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: '14px',
                color: '#666',
                marginBottom: '8px',
              }}
            >
              上記のサービスが・・・
            </div>
            <div style={{ marginBottom: '4px' }}>
              <span
                style={{
                  fontSize: '36px',
                  fontWeight: 'bold',
                }}
              >
                ¥{plan.monthly_fee.toLocaleString()}
              </span>
            </div>
            <div style={{ fontSize: '13px', color: '#888' }}>
              月額（税込）・1日あたり約
              {Math.round(plan.monthly_fee / 30)}円
            </div>
          </div>
        </div>

        {/* ===== 3つのPoint ===== */}
        <div style={{ paddingBottom: '24px' }}>
          <SectionHead enTitle="POINT" jaTitle="3つのPoint" />
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '20px',
              textAlign: 'center',
            }}
          >
            <img
              src={pointsBadge}
              alt="故障・キズ・不動も安心"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                marginBottom: '16px',
                borderRadius: '8px',
              }}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-around',
                gap: '8px',
              }}
            >
              {[
                { label: '故障', note: 'も安心' },
                { label: 'キズ', note: 'も安心' },
                { label: '不動', note: 'でも安心' },
              ].map((pt) => (
                <div key={pt.label} style={{ textAlign: 'center' }}>
                  <div
                    style={{
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: '#1E55E6',
                    }}
                  >
                    {pt.label}
                  </div>
                  <div style={{ fontSize: '12px', color: '#555' }}>
                    {pt.note}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ===== S4: 対象カテゴリ ===== */}
        <div
          style={{
            margin: '0 -16px',
            padding: '24px 16px',
            backgroundColor: '#f6f5f2',
          }}
        >
          <SectionHead enTitle="COVERAGE" jaTitle="サービスの対象カテゴリ" />
          <p
            style={{
              fontSize: '13px',
              color: '#666',
              marginBottom: '16px',
              textAlign: 'center',
            }}
          >
            下記の「対象カテゴリ内」の修理・補修に会員価格を適用できます
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
              marginBottom: '24px',
            }}
          >
            {targetCategories.map((cat) => (
              <div
                key={cat}
                style={{
                  padding: '16px',
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                }}
              >
                {cat}
              </div>
            ))}
          </div>

          {/* 修理事例 */}
          <SectionHead enTitle="CASE" jaTitle="修理事例" />
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            {repairExamples.map((ex, i) => (
              <div
                key={i}
                style={{
                  padding: '20px',
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                }}
              >
                <div
                  style={{
                    fontSize: '15px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                  }}
                >
                  {ex.title}
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#888',
                    marginBottom: '4px',
                  }}
                >
                  {ex.car}
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#888',
                    marginBottom: '12px',
                  }}
                >
                  {ex.mileage}
                </div>
                <div
                  style={{
                    fontSize: '13px',
                    color: '#555',
                    marginBottom: '12px',
                  }}
                >
                  {ex.detail}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '16px',
                    padding: '12px',
                    backgroundColor: '#FFF8E1',
                    borderRadius: '8px',
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: '#888' }}>
                      一般修理費用
                    </div>
                    <div
                      style={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: '#C62828',
                        textDecoration: 'line-through',
                      }}
                    >
                      ¥{ex.originalPrice.toLocaleString()}
                    </div>
                  </div>
                  <div style={{ fontSize: '20px', color: '#888' }}>→</div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: '#888' }}>
                      会員価格
                    </div>
                    <div
                      style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: '#1E55E6',
                      }}
                    >
                      ¥1,000
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ===== S5: 実績・信頼 ===== */}
        <div
          style={{
            margin: '0 -16px',
            padding: '32px 16px',
            backgroundColor: '#f6f5f2',
            color: '#333',
          }}
        >
          <SectionHead
            enTitle="VOICE"
            jaTitle="お客様の声"
            accentColor="#1E55E6"
          />

          {/* お客様の声 */}
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            {customerVoices.map((voc, i) => (
              <div
                key={i}
                style={{
                  padding: '20px',
                  border: '1px solid #ccc',
                  borderRadius: '12px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                  }}
                >
                  <div
                    style={{
                      width: '3px',
                      minHeight: '40px',
                      backgroundColor: '#1E55E6',
                      borderRadius: '2px',
                      flexShrink: 0,
                    }}
                  />
                  <div>
                    <div
                      style={{
                        fontSize: '15px',
                        fontWeight: 'bold',
                        marginBottom: '8px',
                      }}
                    >
                      {voc.title}
                    </div>
                    <div
                      style={{
                        fontSize: '13px',
                        color: '#555',
                        lineHeight: 1.7,
                        marginBottom: '12px',
                      }}
                    >
                      「{voc.body}」
                    </div>
                    <div style={{ fontSize: '12px', color: '#888' }}>
                      {voc.profile}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ===== FAQ ===== */}
        <div
          style={{
            margin: '0 -16px',
            padding: '24px 16px',
            backgroundColor: '#f6f5f2',
          }}
        >
          <SectionHead enTitle="FAQ" jaTitle="よくある質問" />
          {faqItems.map((faq, i) => (
            <div
              key={i}
              style={{
                borderBottom: '1px solid #e0e0e0',
                overflow: 'hidden',
              }}
            >
              <div
                onClick={() => toggleFaq(i)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '14px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  minHeight: '44px',
                }}
              >
                <span>Q. {faq.question}</span>
                <span
                  style={{
                    transform:
                      openFaqIndex === i ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.2s',
                    fontSize: '12px',
                    flexShrink: 0,
                    marginLeft: '8px',
                  }}
                >
                  ▼
                </span>
              </div>
              {openFaqIndex === i && (
                <div
                  style={{
                    padding: '0 0 14px',
                    fontSize: '13px',
                    color: '#666',
                    lineHeight: 1.7,
                  }}
                >
                  A.{' '}
                  {faq.answer ??
                    `月額¥${plan.monthly_fee.toLocaleString()}（税込）です。修理を利用しない月も定額のお支払いとなります。`}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* フッター分の余白 */}
        <div style={{ height: '80px' }} />
      </div>

      {/* 固定フッターCTA */}
      <div
        style={{
          position: 'fixed',
          bottom: '90px',
          left: 0,
          right: 0,
          padding: '12px 16px',
          backgroundColor: '#fff',
          borderTop: '1px solid #e0e0e0',
          zIndex: 100,
        }}
      >
        <button
          onClick={handleClickStart}
          style={{
            width: '100%',
            padding: '14px',
            fontSize: '15px',
            fontWeight: 'bold',
            color: '#fff',
            backgroundColor: '#1E55E6',
            border: 'none',
            borderRadius: '28px',
            cursor: 'pointer',
          }}
        >
          {plan.plan_name}をはじめる
        </button>
      </div>
    </PageTemplate>
  );
};
